import { DoubleSide, Mesh, PlaneBufferGeometry, ShaderMaterial, Vector4 } from 'three';
import { Vector2 } from 'three';
import { SphereItemType } from 'shared/interfaces/planogram';
import { BorderSide } from 'shared/interfaces/planogram';
import standardVertexShader from 'shared/renderingEngine/shaders/StandardVertexShader.glsl';
import shapeFragmentShader from 'shared/renderingEngine/shaders/ShapeFragmentShader.glsl';
import BaseObject3D from 'shared/object3d/BaseObject3D';
import { hexToRgbaArray, resolvePaletteColor } from 'shared/utils/colors';
const UNSET_COLOR = new Vector4(1, 0, 0, 1);
function prepareColor(colorHex, opacity, palette) {
    return new Vector4(...hexToRgbaArray(resolvePaletteColor(colorHex, palette), [0, 0, 0], opacity));
}
function isBorderVisible(shapeConfig) {
    return shapeConfig.borderOpacity > 0 && shapeConfig.thickness > 0;
}
function isFillVisible(shapeConfig) {
    return shapeConfig.fillOpacity > 0 && shapeConfig.scale[0] > 0 && shapeConfig.scale[1] > 0;
}
function isShapeVisible(shapeConfig) {
    return isBorderVisible(shapeConfig) || isFillVisible(shapeConfig);
}
function hasCornerRadius(shapeConfig) {
    return shapeConfig.cornerRadius.some(it => it > 0);
}
const geometry = new PlaneBufferGeometry(1, 1, 1);
class ShapeObject3D extends BaseObject3D {
    constructor() {
        super();
        this.renderScale = new Vector2();
        this.boundsScale = new Vector2();
        this.itemType = SphereItemType.Shape;
    }
    getRenderScale(output) {
        return output.copy(this.renderScale);
    }
    getBoundsScale(output) {
        return output.copy(this.boundsScale);
    }
    updateScale(scale, shapeConfig) {
        this.boundsScale = scale.clone();
        this.renderScale = scale.clone();
        switch (shapeConfig.borderSide) {
            case BorderSide.Inside:
                break;
            case BorderSide.Centered:
                this.renderScale.addScalar(2 * shapeConfig.thickness);
                break;
            case BorderSide.Outside:
                this.renderScale.addScalar(4 * shapeConfig.thickness);
                break;
        }
        this.userData.mesh.material.uniforms.scale.value = this.renderScale;
        const mesh = this.userData.mesh;
        const relativeScale = this.renderScale.clone().divide(scale);
        mesh.scale.set(relativeScale.x, relativeScale.y, 1);
    }
    configure(shapeConfig) {
        const defines = {};
        if (isBorderVisible(shapeConfig))
            defines.BORDER = '';
        const uniforms = {
            opacity: { value: 1 },
            borderSize: { value: shapeConfig.thickness * 2 }, // the previous implementation rendered the border at twice the size listed in the UI
            borderColor: { value: UNSET_COLOR },
            fillColor: { value: UNSET_COLOR },
            scale: { value: this.renderScale },
            cornerRadius: { value: new Vector4(...shapeConfig.cornerRadius) },
        };
        const material = new ShaderMaterial({
            vertexShader: standardVertexShader,
            fragmentShader: shapeFragmentShader,
            depthTest: false,
            uniforms,
            defines,
            transparent: true,
            side: DoubleSide,
        });
        const mesh = new Mesh(geometry, material);
        mesh.name = 'ShapeMesh';
        this.userData.mesh = mesh;
        this.add(mesh);
        const scale = new Vector2(shapeConfig.scale[0], shapeConfig.scale[1]);
        this.updateScale(scale, shapeConfig);
    }
    rebuild(shapeConfig, palette) {
        const scale = new Vector2(shapeConfig.scale[0], shapeConfig.scale[1]);
        this.updateScale(scale, shapeConfig);
        this.visibilityOptimisation(isShapeVisible(shapeConfig));
        const material = this.userData.mesh.material;
        const enableBorder = isBorderVisible(shapeConfig);
        const oldEnableBorder = material.defines.BORDER !== undefined;
        if (enableBorder !== oldEnableBorder) {
            material.defines.BORDER = enableBorder ? '' : undefined;
            material.needsUpdate = true;
        }
        const enableCornerRadius = hasCornerRadius(shapeConfig);
        const oldEnableCornerRadius = material.defines.CORNER_RADIUS !== undefined;
        if (enableCornerRadius !== oldEnableCornerRadius) {
            material.defines.CORNER_RADIUS = enableCornerRadius ? '' : undefined;
            material.needsUpdate = true;
        }
        this.setMaterialUniforms(this.configToUniforms(shapeConfig, palette));
    }
    visibilityOptimisation(visible) {
        if (this.itemType !== SphereItemType.Shape)
            return;
        this.visible = visible;
        this.userData.mesh.visible = visible;
    }
    overrideOpacity(opacity) {
        this.visibilityOptimisation(opacity > 0);
        const material = this.userData.mesh.material;
        const shading = material.uniforms.shading;
        if ((shading === null || shading === void 0 ? void 0 : shading.value) !== undefined && material.userData.tempOpacityApplied !== true) {
            material.userData.originalOpacity = shading.value.w;
            shading.value = shading.value.clone().setW(opacity);
            material.userData.tempOpacityApplied = true;
        }
    }
    resetOpacity() {
        const material = this.userData.mesh.material;
        this.visibilityOptimisation(material.userData.originalOpacity > 0);
        const shading = material.uniforms.shading;
        if ((shading === null || shading === void 0 ? void 0 : shading.value) !== undefined && material.userData.tempOpacityApplied === true) {
            shading.value = shading.value.clone().setW(material.userData.originalOpacity);
            material.userData.tempOpacityApplied = false;
        }
    }
    setMaterialUniforms(uniforms) {
        const material = this.userData.mesh.material;
        Object.entries(uniforms).forEach(([name, uniform]) => {
            material.uniforms[name].value = uniform.value;
        });
    }
    configToUniforms(shapeConfig, palette) {
        const uniforms = {
            opacity: { value: 1 },
            borderSize: { value: shapeConfig.thickness * 2 }, // the previous implementation rendered the border at twice the size listed in the UI
            borderColor: {
                value: prepareColor(shapeConfig.borderColor, shapeConfig.borderOpacity, palette),
            },
            fillColor: { value: prepareColor(shapeConfig.fillColor, shapeConfig.fillOpacity, palette) },
            scale: { value: this.renderScale },
            cornerRadius: { value: new Vector4(...shapeConfig.cornerRadius) },
        };
        return uniforms;
    }
    dispose() {
        const mesh = this.userData.mesh;
        mesh === null || mesh === void 0 ? void 0 : mesh.material.dispose();
        mesh === null || mesh === void 0 ? void 0 : mesh.geometry.dispose();
    }
}
export default ShapeObject3D;
